import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Plug,
  FileCheck,
  MousePointerClick,
  Rocket,
  Eye,
  Target,
  Search,
  Bot,
  Mic,
  Upload,
  FileText,
  Zap,
  Building2,
  Database,
  GitBranch,
  Shield,
  LayoutDashboard,
  CheckCircle2,
  AlertCircle,
  FolderSearch,
  FolderOpen,
  MessageSquareX
} from 'lucide-react';
import { PageLayout } from '@/components';
import { MarketingHero } from '@/components/ui';
import DemoRequestModal from '@/pages/free-trial/DemoRequestModal';

const ProductFocus: React.FC = () => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'demo' | 'trial'>('demo');

  const openModal = (type: 'demo' | 'trial') => {
    setModalType(type);
    setModalOpen(true);
  };

  const documentationHierarchy = [
    {
      title: t('product.hierarchyVisionTitle'),
      desc: t('product.hierarchyVisionDesc'),
      icon: Target,
      color: 'blue'
    },
    {
      title: t('product.hierarchySystemTitle'),
      desc: t('product.hierarchySystemDesc'),
      icon: LayoutDashboard,
      color: 'emerald'
    },
    {
      title: t('product.hierarchySolutionTitle'),
      desc: t('product.hierarchySolutionDesc'),
      icon: GitBranch,
      color: 'purple'
    },
    {
      title: t('product.hierarchyAdrTitle'),
      desc: t('product.hierarchyAdrDesc'),
      icon: FileText,
      color: 'amber'
    }
  ];

  const features = [
    { icon: Plug, text: t('product.featureIntegrates') },
    { icon: FileCheck, text: t('product.featureSingleSource') },
    { icon: MousePointerClick, text: t('product.featureFindContext') }
  ];

  const keyFeatures = [
    { icon: Rocket, text: t('product.featureIdentifyMissing') },
    { icon: Eye, text: t('product.featureIncreaseVisibility') },
    { icon: Target, text: t('product.featureMeasureCoverage') },
    { icon: Search, text: t('product.featureDetectGaps') }
  ];

  const painPoints = [
    {
      icon: FolderSearch,
      text: t('product.painScattered')
    },
    {
      icon: MessageSquareX,
      text: t('product.painLockedContext')
    },
    {
      icon: AlertCircle,
      text: t('product.painReconstructing')
    }
  ];

  return (
    <PageLayout>
      <div>
        {/* Hero Section */}
        <MarketingHero variant="hero" intensity="normal" className="pt-20 pb-16 sm:pt-24">
          <div className="container mx-auto px-6 max-w-6xl">
            <div className="text-center mb-10">
              <span className="inline-block px-4 py-2 bg-blue-500/20 text-blue-300 rounded-full text-sm font-medium mb-6">
                {t('product.heroBadge')}
              </span>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6 leading-tight text-white">
                {t('product.heroTitle')}
                <br className="hidden sm:block" />{' '}
                <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  {t('product.heroTitleHighlight')}
                </span>
              </h1>

              <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto mb-8 leading-relaxed">
                {t('product.heroSubtitle')}
              </p>

              {/* Feature Pills */}
              <div className="flex flex-wrap justify-center gap-3 mb-10">
                {features.map((feature, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-slate-200"
                  >
                    <feature.icon size={18} className="text-blue-400" />
                    <span className="text-sm font-medium">{feature.text}</span>
                  </div>
                ))}
              </div>

              {/* Primary CTA */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button
                  onClick={() => openModal('demo')}
                  className="group px-8 py-4 bg-white text-slate-900 rounded-xl font-semibold text-lg transition-all hover:bg-slate-100 flex items-center justify-center gap-3 transform hover:scale-[1.02] active:scale-95"
                >
                  {t('product.talkToSales')}
                  <ArrowRight
                    size={20}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </button>
              </div>
            </div>
          </div>
        </MarketingHero>

        {/* Enterprise Architecture & Hierarchy */}
        <section className="py-16 bg-gradient-to-br from-blue-50 via-white to-cyan-50">
          <div className="container mx-auto px-6 max-w-6xl">
            {/* Header + Pain Points */}
            <div className="text-center mb-12">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-full text-sm font-medium mb-4 shadow-sm">
                <FolderOpen className="w-4 h-4" />
                {t('product.eaPlatformBadge')}
              </span>

              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {t('product.eaPlatformTitle')}
              </h2>
              <p className="text-lg text-slate-600 max-w-3xl mx-auto mb-6">
                {t('product.eaPlatformSubtitle')}
              </p>

              <div className="max-w-3xl mx-auto">
                <p className="text-slate-500 text-xs font-semibold tracking-[0.16em] uppercase mb-3">
                  {t('product.soundFamiliar')}
                </p>
                <div className="grid sm:grid-cols-3 gap-4">
                  {painPoints.map((point, i) => (
                    <div
                      key={i}
                      className="flex flex-col items-center gap-2 p-4 bg-white/80 rounded-xl border border-slate-200 shadow-sm"
                    >
                      <div className="w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center mb-1">
                        <point.icon size={20} className="text-amber-500" />
                      </div>
                      <span className="text-slate-700 text-sm text-center">
                        {point.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-start">
              {/* Hierarchy Visual */}
              <div className="space-y-4 relative">
                <div className="hidden lg:block absolute left-5 top-0 bottom-0 w-px bg-slate-200" />
                {documentationHierarchy.map((item, i) => (
                  <div
                    key={i}
                    className={`relative flex items-start gap-4 p-5 bg-white/90 rounded-xl border-2 shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5 ${
                      item.color === 'blue'
                        ? 'border-blue-200 hover:border-blue-300'
                        : item.color === 'emerald'
                        ? 'border-emerald-200 hover:border-emerald-300 lg:ml-4'
                        : item.color === 'purple'
                        ? 'border-purple-200 hover:border-purple-300 lg:ml-8'
                        : 'border-amber-200 hover:border-amber-300 lg:ml-12'
                    }`}
                  >
                    <div className="hidden lg:block absolute -left-5 top-6 w-3 h-3 rounded-full bg-white border border-slate-300" />
                    <div
                      className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${
                        item.color === 'blue'
                          ? 'bg-blue-100'
                          : item.color === 'emerald'
                          ? 'bg-emerald-100'
                          : item.color === 'purple'
                          ? 'bg-purple-100'
                          : 'bg-amber-100'
                      }`}
                    >
                      <item.icon
                        size={24}
                        className={
                          item.color === 'blue'
                            ? 'text-blue-600'
                            : item.color === 'emerald'
                            ? 'text-emerald-600'
                            : item.color === 'purple'
                            ? 'text-purple-600'
                            : 'text-amber-600'
                        }
                      />
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 mb-1">
                        {t('product.level', { number: i + 1 })}
                      </p>
                      <h3 className="font-semibold text-slate-900 text-lg mb-1">
                        {item.title}
                      </h3>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Key Benefits of Hierarchy */}
              <div className="space-y-6">
                <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-md">
                  <h3 className="font-semibold text-slate-900 mb-4 text-lg">
                    {t('product.governanceTitle')}
                  </h3>
                  <ul className="space-y-4 text-sm">
                    <li className="flex items-start gap-3 text-slate-600">
                      <CheckCircle2
                        size={20}
                        className="text-emerald-500 mt-0.5 flex-shrink-0"
                      />
                      <span>
                        <strong className="text-slate-900">{t('product.governanceSolutionDesigns')}</strong> {t('product.governanceSolutionDesignsDesc')}
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-slate-600">
                      <CheckCircle2
                        size={20}
                        className="text-emerald-500 mt-0.5 flex-shrink-0"
                      />
                      <span>
                        <strong className="text-slate-900">{t('product.governanceAdrs')}</strong> {t('product.governanceAdrsDesc')}
                      </span>
                    </li>
                    <li className="flex items-start gap-3 text-slate-600">
                      <CheckCircle2
                        size={20}
                        className="text-emerald-500 mt-0.5 flex-shrink-0"
                      />
                      <span>
                        <strong className="text-slate-900">{t('product.governanceSystemArch')}</strong> {t('product.governanceSystemArchDesc')}
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-6 text-white shadow-lg">
                  <h3 className="font-semibold mb-3 text-lg">{t('product.whyEnterpriseTitle')}</h3>
                  <p className="text-slate-200 text-sm leading-relaxed mb-3">
                    {t('product.whyEnterpriseParagraph1')}
                  </p>
                  <p className="text-slate-300 text-sm leading-relaxed">
                    {t('product.whyEnterpriseParagraph2')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Key Features Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-6 max-w-6xl">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {t('product.keyFeaturesTitle')}
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                {t('product.keyFeaturesSubtitle')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {keyFeatures.map((feature, i) => (
                <div
                  key={i}
                  className="group flex items-start gap-4 p-6 bg-slate-50 border border-slate-200 rounded-2xl hover:border-blue-300 hover:shadow-lg hover:shadow-blue-100/50 transition-all duration-300"
                >
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <feature.icon size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-lg font-medium text-slate-900">{feature.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Meeting-to-Documentation Deep Dive */}
        <MarketingHero variant="cta" intensity="normal" className="py-16">
          <div className="container mx-auto px-6 max-w-6xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 text-blue-300 rounded-full text-sm font-medium mb-6">
                  <Mic size={16} />
                  {t('product.meetingsBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
                  {t('product.meetingsTitle')}
                </h2>
                <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                  {t('product.meetingsSubtitle')}
                </p>

                <div className="grid sm:grid-cols-2 gap-4 mb-8">
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <Mic size={20} className="text-blue-400" />
                      <span className="text-white font-medium">{t('product.recordLive')}</span>
                    </div>
                    <p className="text-slate-400 text-sm">
                      {t('product.recordLiveDesc')}
                    </p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <Upload size={20} className="text-purple-400" />
                      <span className="text-white font-medium">{t('product.importAudio')}</span>
                    </div>
                    <p className="text-slate-400 text-sm">
                      {t('product.importAudioDesc')}
                    </p>
                  </div>
                </div>

                <Link
                  to="/add"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-slate-900 rounded-xl font-semibold hover:bg-slate-100 transition-colors"
                >
                  {t('product.tryRecordingFlow')}
                  <ArrowRight size={18} />
                </Link>
              </div>

              {/* AI Demo Visual */}
              <div className="relative">
                <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 backdrop-blur-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <Bot size={24} className="text-blue-400" />
                    <span className="text-white font-medium">{t('product.aiAssistant')}</span>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-slate-700/50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Mic size={16} className="text-blue-400" />
                        <span className="text-blue-400 text-sm font-medium">
                          {t('product.fromMeetingRecording')}
                        </span>
                      </div>
                      <p className="text-slate-300 text-sm">
                        &quot;{t('product.meetingExcerpt')}&quot;
                      </p>
                    </div>
                    <div className="flex justify-center">
                      <ArrowRight size={20} className="text-slate-500" />
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText size={16} className="text-emerald-400" />
                        <span className="text-emerald-400 text-sm font-medium">
                          {t('product.generatedAdrDraft')}
                        </span>
                      </div>
                      <p className="text-slate-300 text-sm font-medium">
                        {t('product.adrExample')}
                      </p>
                      <p className="text-slate-400 text-xs mt-2">
                        {t('product.adrSections')}
                      </p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Zap size={16} className="text-amber-400" />
                        <span className="text-amber-400 text-sm font-medium">{t('product.autoLinked')}</span>
                      </div>
                      <p className="text-slate-300 text-sm">
                        {t('product.autoLinkedOrder')}
                        <br />
                        {t('product.autoLinkedInitiative')}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-blue-500/20 rounded-full blur-2xl" />
                <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-purple-500/20 rounded-full blur-2xl" />
              </div>
            </div>
          </div>
        </MarketingHero>

        {/* Enterprise Edition Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-6 max-w-6xl">
            <div className="text-center mb-10">
              <span className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-4">
                {t('product.enterpriseBadge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {t('product.enterpriseTitle')}
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                {t('product.enterpriseSubtitle')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Database size={28} className="text-purple-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900">
                    {t('product.selfManagedTitle')}
                  </h3>
                </div>
                <p className="text-slate-600 mb-6">
                  {t('product.selfManagedDesc')}
                </p>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-center gap-3 text-slate-600">
                    <Shield size={18} className="text-emerald-500" />
                    <span>{t('product.selfManagedResidency')}</span>
                  </li>
                  <li className="flex items-center gap-3 text-slate-600">
                    <Building2 size={18} className="text-emerald-500" />
                    <span>{t('product.selfManagedAudit')}</span>
                  </li>
                  <li className="flex items-center gap-3 text-slate-600">
                    <GitBranch size={18} className="text-emerald-500" />
                    <span>{t('product.selfManagedSso')}</span>
                  </li>
                </ul>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Plug size={28} className="text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900">{t('product.connectorsTitle')}</h3>
                </div>
                <p className="text-slate-600 mb-6">
                  {t('product.connectorsDesc')}
                </p>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-center gap-3 text-slate-600">
                    <FileText size={18} className="text-emerald-500" />
                    <span>{t('product.connectorsWikis')}</span>
                  </li>
                  <li className="flex items-center gap-3 text-slate-600">
                    <Search size={18} className="text-emerald-500" />
                    <span>{t('product.connectorsRepos')}</span>
                  </li>
                  <li className="flex items-center gap-3 text-slate-600">
                    <Zap size={18} className="text-emerald-500" />
                    <span>{t('product.connectorsApi')}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-16 bg-gradient-to-br from-blue-50 via-white to-cyan-50">
          <div className="container mx-auto px-6 max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('product.finalCtaTitle')}
            </h2>
            <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
              {t('product.finalCtaSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => openModal('demo')}
                className="group px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-cyan-500 text-white rounded-xl font-semibold text-lg transition-all shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 flex items-center justify-center gap-3 transform hover:scale-[1.02] active:scale-95"
              >
                {t('product.requestEnterpriseDemo')}
              <ArrowRight
                  size={20}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </button>
            </div>
          </div>
        </section>

        <DemoRequestModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          requestType={modalType}
        />
      </div>
    </PageLayout>
  );
};

export default ProductFocus;
